import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { destinationDetails } from "../../mocks/destinationDetails";
import DestinationDetailModal from "./components/DestinationDetailModal";
import SelfDriveInfoSection from "./components/SelfDriveInfoSection";
import PricingInfoSection from "./components/PricingInfoSection";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDestination, setActiveDestination] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showAllDestinations, setShowAllDestinations] = useState(false);
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [destPage, setDestPage] = useState(0);
  const [regionFilter, setRegionFilter] = useState("全部");
  const [difficultyFilter, setDifficultyFilter] = useState("全部");
  const [durationFilter, setDurationFilter] = useState("全部");

  const DEST_PER_PAGE = 4;

  const regionMap: Record<number, string> = {
    1: "西藏/四川", 2: "西北", 3: "新疆", 4: "華南/東南",
    5: "西南", 6: "東北/內蒙古", 7: "西北", 8: "華南/東南",
    9: "西藏/四川", 10: "東北/內蒙古", 11: "華南/東南", 12: "西藏/四川",
    13: "西藏/四川", 14: "新疆", 15: "西北", 16: "西南",
    17: "西藏/四川", 18: "西北", 19: "華南/東南", 20: "華南/東南",
  };

  const parseDays = (duration: string): number => {
    const match = duration.match(/(\d+)天/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const chinaDestinationDetails = destinationDetails.filter((d) => d.id <= 20);
  const europeDestinationDetails = destinationDetails.filter((d) => d.id > 20);

  const filteredChina = chinaDestinationDetails.filter((dest) => {
    if (regionFilter !== "全部" && regionMap[dest.id] !== regionFilter) return false;
    if (difficultyFilter !== "全部" && dest.difficulty !== difficultyFilter) return false;
    if (durationFilter !== "全部") {
      const days = parseDays(dest.duration);
      if (durationFilter === "1-5天" && days > 5) return false;
      if (durationFilter === "6-8天" && (days < 6 || days > 8)) return false;
      if (durationFilter === "9天以上" && days < 9) return false;
    }
    return true;
  });

  const totalDestPages = Math.ceil(filteredChina.length / DEST_PER_PAGE);

  const heroRef = useRef<HTMLDivElement>(null);
  const destinationsRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "https://example.com";

    const travelAgencyScript = document.createElement("script");
    travelAgencyScript.type = "application/ld+json";
    travelAgencyScript.id = "home-travel-agency-jsonld";
    travelAgencyScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TravelAgency",
      "name": "Chillgo",
      "url": siteUrl,
      "logo": "https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png",
      "description": "香港首屈一指的自駕遊平台，提供川藏線、新疆、歐洲等頂級自駕路線及全自訂行程服務。",
      "telephone": "+852-5348-2337",
      "email": "chillgoooo@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "彌敦道 688號 18樓 1801室",
        "addressLocality": "旺角",
        "addressRegion": "九龍",
        "addressCountry": "HK",
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "22.3162",
        "longitude": "114.1694",
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "10:00",
          "closes": "19:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Saturday", "Sunday"],
          "opens": "11:00",
          "closes": "17:00",
        },
      ],
      "sameAs": [
        "https://www.facebook.com/profile.php?id=61582054874473",
        "https://www.instagram.com/chill_goooo",
      ],
    });
    document.head.appendChild(travelAgencyScript);

    const websiteScript = document.createElement("script");
    websiteScript.type = "application/ld+json";
    websiteScript.id = "home-website-jsonld";
    websiteScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Chillgo",
      "url": siteUrl,
      "description": "香港首屈一指的自駕遊平台，提供川藏線、新疆、歐洲等頂级自駕路線及全自訂行程服務。",
      "inLanguage": "zh-HK",
      "publisher": {
        "@type": "Organization",
        "name": "Chillgo",
        "logo": {
          "@type": "ImageObject",
          "url": "https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png",
        },
      },
    });
    document.head.appendChild(websiteScript);

    return () => {
      document.getElementById("home-travel-agency-jsonld")?.remove();
      document.getElementById("home-website-jsonld")?.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const DEADLINE = new Date("2026-04-10T23:59:59");

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = DEADLINE.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const destinations = filteredChina.slice(
    destPage * DEST_PER_PAGE,
    destPage * DEST_PER_PAGE + DEST_PER_PAGE
  );
  const allDestinations = destinationDetails;

  const testimonials = [
    {
      id: 1,
      name: "張先生",
      avatar: "https://public.readdy.ai/ai/img_res/b8a717f7fdce9ca252c1998c6d347307.jpg",
      rating: 5,
      text: "川藏線之旅讓我終生難忘！領隊非常專業，每個細節都安排得很周到。第一次看到珠峰的那一刻，我熱淚盈眶。",
      trip: "川藏線10日深度遊",
    },
    {
      id: 2,
      name: "李女士",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Chinese%20young%20woman%20with%20bright%20smile%2C%20modern%20casual%20style%2C%20clean%20background%2C%20natural%20lighting%2C%20high%20quality%20portrait%20photography&width=100&height=100&seq=202&orientation=squarish",
      rating: 5,
      text: "帶孩子去青海湖，客服推薦的親子路線非常棒！小朋友玩得很開心，我們也拍了很多美照。下次還會選擇這裡。",
      trip: "青海湖親子遊",
    },
    {
      id: 3,
      name: "王先生",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Chinese%20young%20man%20with%20confident%20expression%2C%20outdoor%20adventure%20style%2C%20clean%20background%2C%20natural%20lighting%2C%20high%20quality%20portrait%20photography&width=100&height=100&seq=203&orientation=squarish",
      rating: 5,
      text: "獨庫公路自駕簡直太震撼了！一天之內經歷了四季變化，風景美得讓人窒息。車輛性能很好，全程無憂。",
      trip: "獨庫公路穿越之旅",
    },
  ];

  const galleryImages = [
    "https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/d7368ceb91c0854d2100deeb7ddf24ae.jpeg",
    "https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/e6005bc244dd7990a467ddb7b37b324c.jpeg",
    "https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/aa471496a6d2f69fc6e6a53dd4760944.jpeg",
    "https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/2abbf25031cf3008e21e1fb0d4a8f73c.jpeg",
    "https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/7474c5ea56acf5e8d3d731286f54aba9.jpeg",
    "https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/c225447403308f347852fae0341eaa1c.jpeg",
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const handleBookingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBookingSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d6t256vcrcujsk0v12h0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setBookingSuccess(true);
      form.reset();
    } catch {
      // silent
    } finally {
      setBookingSubmitting(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d6sitj34rjv84lqbqefg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setContactSuccess(true);
      form.reset();
    } catch {
      // silent
    } finally {
      setContactSubmitting(false);
    }
  };

  const selectedDetail = selectedDetailId !== null
    ? destinationDetails.find((d) => d.id === selectedDetailId) ?? null
    : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img
                src="https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png"
                alt="Chill Go Logo"
                className={`h-16 w-auto object-contain transition-all duration-300 ${
                  isScrolled ? "" : "drop-shadow-[0_2px_8px_rgba(255,255,255,0.5)]"
                }`}
              />
            </div>

            <div className="hidden md:flex items-center space-x-8">
              {[
                { name: "首頁", id: "home" },
                { name: "目的地", id: "destinations" },
                { name: "自駕資訊", id: "info" },
                { name: "報價說明", id: "pricing" },
                { name: "預訂", id: "booking" },
                { name: "聯繫", id: "contact" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors hover:text-orange-500 cursor-pointer whitespace-nowrap ${
                    isScrolled ? "text-gray-700" : "text-white/90"
                  }`}
                >
                  {item.name}
                </button>
              ))}
              <Link
                to="/custom-itinerary"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border ${
                  isScrolled
                    ? "border-orange-400 text-orange-500 hover:bg-orange-500 hover:text-white"
                    : "border-white/50 text-white hover:bg-white/10"
                }`}
              >
                <i className="ri-fire-line text-xs"></i>
                <span>自訂行程</span>
              </Link>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowBookingModal(true)}
                className="hidden md:flex bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all items-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>立即預訂</span>
                <i className="ri-arrow-right-line"></i>
              </button>

              {/* Hamburger Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  isScrolled
                    ? "text-gray-900 hover:bg-gray-100"
                    : "text-white hover:bg-white/10"
                }`}
                aria-label="開啟選單"
              >
                <div className="w-5 h-5 flex flex-col justify-center space-y-1.5">
                  <span
                    className={`block h-0.5 bg-current transition-all duration-300 ${
                      isMobileMenuOpen ? "rotate-45 translate-y-2" : ""
                    }`}
                  ></span>
                  <span
                    className={`block h-0.5 bg-current transition-all duration-300 ${
                      isMobileMenuOpen ? "opacity-0 scale-x-0" : ""
                    }`}
                  ></span>
                  <span
                    className={`block h-0.5 bg-current transition-all duration-300 ${
                      isMobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
                    }`}
                  ></span>
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Menu Drawer */}
          <div
            className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
              isMobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="bg-white/98 backdrop-blur-md border-t border-gray-100 px-6 py-4">
              <nav className="space-y-1 mb-5">
                {[
                  { name: "首頁", id: "home", icon: "ri-home-line" },
                  { name: "目的地", id: "destinations", icon: "ri-map-pin-line" },
                  { name: "自駕資訊", id: "info", icon: "ri-passport-line" },
                  { name: "報價說明", id: "pricing", icon: "ri-money-dollar-circle-line" },
                  { name: "預訂", id: "booking", icon: "ri-calendar-check-line" },
                  { name: "聯繫", id: "contact", icon: "ri-phone-line" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors text-left cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <span className="font-medium">{item.name}</span>
                    <i className="ri-arrow-right-s-line ml-auto text-gray-400"></i>
                  </button>
                ))}
                {/* Custom Itinerary Link */}
                <Link
                  to="/custom-itinerary"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-orange-500 text-white font-medium cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-fire-line text-lg"></i>
                  </div>
                  <span className="font-medium">自訂行程（最熱門）</span>
                  <i className="ri-arrow-right-s-line ml-auto text-orange-300"></i>
                </Link>
              </nav>

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setShowBookingModal(true);
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-full font-medium transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line"></i>
                <span>立即預訂行程</span>
              </button>

              <p className="text-center text-xs text-gray-400 mt-3">
                專業顧問 · 24小時服務 · 全程保障
              </p>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/aa471496a6d2f69fc6e6a53dd4760944.jpeg"
            alt="自驾游背景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
        </div>

        <div className="relative w-full px-6 lg:px-12 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
              <span className="text-white/90 text-sm">香港本地出發 · 全程自駕</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              <div>馳騁山海間</div>
              <div>自由在路上</div>
            </h1>

            <p className="text-xl text-white/80 mb-10 max-w-xl">
              由香港出發，探索中國最美自駕路線乃至歐洲極限公路，專業領隊全程護航，讓每一次出發都成為難忘的冒險
            </p>

            {/* Early Bird Countdown */}
            <div className="mb-10 max-w-xl">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse inline-block"></span>
                      <span>限時早鳥優惠</span>
                    </div>
                    <span className="text-white/80 text-sm">預訂即享</span>
                    <span className="text-orange-400 font-bold text-base">9折</span>
                    <span className="text-white/60 text-xs">優惠截止於：</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {[
                      { value: countdown.days, label: "天" },
                      { value: countdown.hours, label: "時" },
                      { value: countdown.minutes, label: "分" },
                      { value: countdown.seconds, label: "秒" },
                    ].map((unit, idx) => (
                      <div key={unit.label} className="flex items-center gap-1">
                        <div className="flex flex-col items-center">
                          <div className="bg-white/20 backdrop-blur-sm text-white font-bold text-lg w-11 h-11 flex items-center justify-center rounded-lg tabular-nums leading-none">
                            {String(unit.value).padStart(2, "0")}
                          </div>
                          <span className="text-white/50 text-xs mt-1">{unit.label}</span>
                        </div>
                        {idx < 3 && (
                          <span className="text-orange-400 font-bold text-xl mb-4 leading-none">:</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection("destinations")}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full text-lg font-medium transition-all flex items-center space-x-2"
              >
                <span>探索路線</span>
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full text-lg font-medium transition-all flex items-center space-x-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line text-xl"></i>
                <span>免費諮詢</span>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-white/20">
              {[
                { number: "2000+", label: "精選路線" },
                { number: "24小時", label: "全程支援" },
                { number: "3天", label: "最快出發" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-white/60 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-stone-50">
        <div className="w-full px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase mb-4 block">
                為什麼選擇我們
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                <div>專業的自駕旅遊服務</div>
                <div>讓旅途更安心</div>
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                由香港出發，深耕自駕遊領域十餘年。無論是中國山川大地還是歐洲極限公路，我們擁有豐富的路線資源與專業服務團隊，為您提供一站式的自駕旅行解決方案。
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: "ri-route-line",
                    title: "精選路線",
                    desc: "每條路線都經過實地考察，確保風景優美、路況良好",
                  },
                  {
                    icon: "ri-shield-check-line",
                    title: "安全保障",
                    desc: "專業領隊全程陪同，提供車輛偵測與緊急救援服務",
                  },
                  {
                    icon: "ri-hotel-line",
                    title: "品質住宿",
                    desc: "選沿途優質飯店及特色民宿，保證休息品質",
                  },
                ].map((feature) => (
                  <div key={feature.title} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${feature.icon} text-orange-500 text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://static.readdy.ai/image/ddaf87430264265a05ec70741a1249b8/3b5bbe51848fd4760c8201db835712c8.jpeg"
                alt="自驾旅行"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-500 text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">15年</div>
                    <div className="text-gray-600 text-sm">行業經驗</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section id="destinations" ref={destinationsRef} className="py-24">
        <div className="w-full px-6 lg:px-12">

          {/* ── China Section ── */}
          <div className="mb-24">
            {/* Section Header */}
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-6">
              <div>
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase mb-4 block">
                  中國自駕路線
                </span>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  由香港出發 · 中國最美自駕路線
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl">
                  從雪山高原到熱帶海島，全程由香港自駕出發，探索不同風格的自駕體驗
                </p>
              </div>

              {/* Arrow Controls */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500 mr-1">
                  {filteredChina.length > 0 ? `${destPage + 1} / ${totalDestPages}` : "0 / 0"}
                </span>
                <button
                  onClick={() => {
                    setDestPage((p) => Math.max(0, p - 1));
                    setActiveDestination(0);
                  }}
                  disabled={destPage === 0 || filteredChina.length === 0}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-100 text-gray-700 hover:bg-orange-500 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-2xl"></i>
                </button>
                <button
                  onClick={() => {
                    setDestPage((p) => Math.min(totalDestPages - 1, p + 1));
                    setActiveDestination(0);
                  }}
                  disabled={destPage >= totalDestPages - 1 || filteredChina.length === 0}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </button>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-stone-50 rounded-2xl p-5 mb-8 space-y-4">
              {[
                {
                  label: "地區",
                  icon: "ri-map-pin-line",
                  key: "region",
                  value: regionFilter,
                  setter: (v: string) => { setRegionFilter(v); setDestPage(0); setActiveDestination(0); },
                  options: ["全部", "西藏/四川", "西北", "新疆", "西南", "東北/內蒙古", "華南/東南"],
                },
                {
                  label: "難度",
                  icon: "ri-bar-chart-line",
                  key: "difficulty",
                  value: difficultyFilter,
                  setter: (v: string) => { setDifficultyFilter(v); setDestPage(0); setActiveDestination(0); },
                  options: ["全部", "輕鬆", "中等", "進階", "地獄"],
                },
                {
                  label: "天數",
                  icon: "ri-calendar-line",
                  key: "duration",
                  value: durationFilter,
                  setter: (v: string) => { setDurationFilter(v); setDestPage(0); setActiveDestination(0); },
                  options: ["全部", "1-5天", "6-8天", "9天以上"],
                },
              ].map((filter) => (
                <div key={filter.key} className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-gray-500 text-sm font-medium w-14 flex-shrink-0">
                    <i className={`${filter.icon} text-base`}></i>
                    <span>{filter.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {filter.options.map((opt) => {
                      const active = filter.value === opt;
                      const diffColor = filter.key === "difficulty"
                        ? opt === "輕鬆" ? "bg-green-500 text-white border-green-500"
                          : opt === "中等" ? "bg-amber-500 text-white border-amber-500"
                          : opt === "進階" ? "bg-red-500 text-white border-red-500"
                          : opt === "地獄" ? "bg-zinc-900 text-orange-400 border-zinc-900"
                          : ""
                        : "";
                      return (
                        <button
                          key={opt}
                          onClick={() => filter.setter(opt)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                            active
                              ? filter.key === "difficulty" && opt !== "全部"
                                ? `${diffColor} border-transparent`
                                : "bg-orange-500 text-white border-orange-500"
                              : "bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-500"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Active filters summary + reset */}
              {(regionFilter !== "全部" || difficultyFilter !== "全部" || durationFilter !== "全部") && (
                <div className="flex items-center justify-between pt-3 border-t border-stone-200">
                  <span className="text-sm text-gray-500">
                    共找到 <strong className="text-orange-500">{filteredChina.length}</strong> 條路線
                  </span>
                  <button
                    onClick={() => {
                      setRegionFilter("全部");
                      setDifficultyFilter("全部");
                      setDurationFilter("全部");
                      setDestPage(0);
                    }}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-orange-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>
                    <span>重置篩選</span>
                  </button>
                </div>
              )}
            </div>

            {/* China Results Grid or Empty State */}
            {filteredChina.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mb-5">
                  <i className="ri-search-line text-stone-400 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">找不到符合條件的路線</h3>
                <p className="text-gray-500 text-sm mb-6">試試調整篩選條件，探索更多精彩目的地</p>
                <button
                  onClick={() => {
                    setRegionFilter("全部");
                    setDifficultyFilter("全部");
                    setDurationFilter("全部");
                    setDestPage(0);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  重置所有篩選
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {destinations.map((dest, index) => (
                    <div
                      key={dest.id}
                      className={`group cursor-pointer ${
                        activeDestination === index ? "lg:col-span-2 lg:row-span-2" : ""
                      }`}
                      onClick={() => setActiveDestination(index)}
                    >
                      <div className="relative h-full min-h-[300px] lg:min-h-[400px] rounded-2xl overflow-hidden">
                        <img
                          src={dest.image}
                          alt={dest.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                        <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                          <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                            {dest.subtitle}
                          </span>
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                            dest.difficulty === "輕鬆" ? "bg-green-500 text-white"
                            : dest.difficulty === "中等" ? "bg-amber-500 text-white"
                            : dest.difficulty === "進階" ? "bg-red-500 text-white"
                            : "bg-zinc-900 text-orange-400"
                          }`}>
                            {dest.difficulty}
                          </span>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <Link
                            to={`/destination/${dest.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block hover:opacity-90 transition-opacity"
                          >
                            <h3 className="text-2xl font-bold text-white mb-2 hover:text-orange-300 transition-colors">
                              {dest.name}
                            </h3>
                          </Link>
                          <p className="text-white/80 text-sm mb-4 line-clamp-2">
                            {dest.description}
                          </p>

                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-baseline gap-1.5">
                              <span className={`font-bold ${dest.price === "需要報價" ? "text-orange-300 text-base" : "text-orange-400 text-xl"}`}>
                                {dest.price}
                              </span>
                              {dest.price !== "需要報價" && (
                                <span className="text-white/60 text-sm">/人起</span>
                              )}
                            </div>
                            <span className="text-white/60 text-sm">{dest.duration}</span>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-4">
                            {dest.features.map((feature) => (
                              <span
                                key={feature}
                                className="text-white/70 text-xs bg-white/10 px-2 py-1 rounded"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDetailId(dest.id);
                              }}
                              className="flex-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-xs font-medium py-2 rounded-lg transition-all flex items-center justify-center space-x-1.5 cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-eye-line text-sm"></i>
                              <span>快速預覽</span>
                            </button>
                            <Link
                              to={`/destination/${dest.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium py-2 rounded-lg transition-all flex items-center justify-center space-x-1.5 cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-arrow-right-up-line text-sm"></i>
                              <span>完整頁面</span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Dot Indicators + CTA */}
                <div className="flex flex-col sm:flex-row items-center justify-between mt-12 gap-5">
                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalDestPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setDestPage(i);
                          setActiveDestination(0);
                        }}
                        className={`transition-all cursor-pointer rounded-full ${
                          i === destPage
                            ? "w-8 h-2.5 bg-orange-500"
                            : "w-2.5 h-2.5 bg-gray-300 hover:bg-orange-300"
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => setShowAllDestinations(true)}
                    className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer"
                  >
                    查看全部目的地
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ── Divider ── */}
          <div className="flex items-center gap-6 mb-24">
            <div className="flex-1 h-px bg-stone-200"></div>
            <div className="flex items-center gap-2 text-stone-400 text-sm font-medium whitespace-nowrap">
              <i className="ri-earth-line text-lg"></i>
              <span>歐洲頂級路線</span>
            </div>
            <div className="flex-1 h-px bg-stone-200"></div>
          </div>

          {/* ── Europe Section ── */}
          <div>
            {/* Europe Header */}
            <div className="mb-10">
              <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase mb-4 block">
                歐洲極限自駕
              </span>
              <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                <div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    由香港出發 · 歐洲地獄難度路線
                  </h2>
                  <p className="text-gray-600 text-lg max-w-2xl">
                    挑戰極限、突破自我，三條由香港直飛出發的頂級歐洲自駕路線
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-zinc-900 text-orange-400 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0">
                  <i className="ri-fire-line"></i>
                  <span>地獄難度 · 非一般挑戰</span>
                </div>
              </div>
            </div>

            {/* Europe Cards — all 3 displayed */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {europeDestinationDetails.map((dest) => (
                <div
                  key={dest.id}
                  className="group cursor-pointer"
                  onClick={() => setSelectedDetailId(dest.id)}
                >
                  <div className="relative h-80 rounded-2xl overflow-hidden mb-5">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {dest.subtitle}
                      </span>
                      <span className="bg-zinc-900 text-orange-400 text-xs font-semibold px-2.5 py-1 rounded-full">
                        {dest.difficulty}
                      </span>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <Link
                        to={`/destination/${dest.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block hover:opacity-90 transition-opacity"
                      >
                        <h3 className="text-2xl font-bold text-white mb-1 hover:text-orange-300 transition-colors">{dest.name}</h3>
                      </Link>
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-1.5">
                          <span className={`font-bold ${dest.price === "需要報價" ? "text-orange-300 text-base" : "text-orange-400 text-xl"}`}>
                            {dest.price}
                          </span>
                          {dest.price !== "需要報價" && (
                            <span className="text-white/60 text-sm">/人起</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-orange-500 text-white text-sm font-medium px-5 py-2.5 rounded-full flex items-center space-x-2">
                        <i className="ri-eye-line"></i>
                        <span>快速預覽</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {dest.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {dest.features.map((feature) => (
                      <span
                        key={feature}
                        className="text-gray-600 text-xs bg-stone-100 px-3 py-1.5 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDetailId(dest.id);
                      }}
                      className="flex-1 border-2 border-zinc-300 text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-eye-line"></i>
                      <span>快速預覽</span>
                    </button>
                    <Link
                      to={`/destination/${dest.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-map-2-line"></i>
                      <span>完整行程</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Europe CTA */}
            <div className="mt-12 bg-zinc-900 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-white font-bold text-xl mb-1">準備好接受地獄挑戰了嗎？</p>
                <p className="text-zinc-400 text-sm">專業領隊全程陪同，讓極限冒險更安全</p>
              </div>
              <button
                onClick={() => setShowBookingModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-full font-semibold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <span>立即諮詢歐洲行程</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Custom Itinerary Promo Banner */}
      <section className="py-16 bg-white">
        <div className="w-full px-6 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl bg-zinc-900">
            <img
              src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20aerial%20view%20of%20solo%20car%20driving%20through%20vast%20golden%20Tibetan%20grassland%20plateau%20at%20sunset%2C%20dramatic%20sky%20with%20orange%20and%20purple%20clouds%2C%20endless%20road%20disappearing%20into%20horizon%2C%20freedom%20adventure%20concept%20photography&width=1400&height=420&seq=831&orientation=landscape"
              alt="自訂行程"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 px-10 py-14">
              <div className="text-center lg:text-left max-w-2xl">
                <div className="inline-flex items-center gap-2 bg-orange-500/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
                  <i className="ri-fire-line"></i>
                  <span>全站最熱門 · 超過 80% 客人選擇</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  不想跟固定團？<br />
                  <span className="text-orange-400">100% 自訂屬於您的行程</span>
                </h2>
                <p className="text-white/75 text-base leading-relaxed">
                  路線、天數、節奏、住宿全部自選。我們處理所有跨境手續，您只需享受旅程。<br />
                  顧問免費設計方案，無限次修改，滿意才確認。
                </p>
                <div className="mt-6 flex flex-wrap gap-5">
                  {[
                    { icon: "ri-refresh-line", text: "無限次免費修改" },
                    { icon: "ri-lock-unlock-line", text: "完全私人出行" },
                    { icon: "ri-time-line", text: "最快 3 天出發" },
                  ].map((p) => (
                    <div key={p.text} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${p.icon} text-orange-400 text-sm`}></i>
                      </div>
                      <span className="text-white/80 text-xs">{p.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-center gap-4 flex-shrink-0">
                <Link
                  to="/custom-itinerary"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all flex items-center space-x-2 cursor-pointer whitespace-nowrap"
                >
                  <span>了解自訂行程</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <p className="text-white/50 text-sm">免費諮詢 · 無需付訂金</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Self Drive Info Section */}
      <SelfDriveInfoSection />

      {/* Pricing Info Section */}
      <PricingInfoSection />

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-stone-50">
        <div className="w-full px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase mb-4 block">
              聯繫我們
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              開始規劃您的專屬之旅
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              無論您有任何問題或想了解行程詳情，我們的專業顧問隨時為您解答
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
            {/* Left Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">聯繫資訊</h3>
                <div className="space-y-5">
                  {[
                    {
                      icon: "ri-phone-line",
                      label: "客服熱線",
                      value: "+852 5348 2337",
                      sub: "週一至週日 09:00–21:00",
                    },
                    {
                      icon: "ri-mail-line",
                      label: "電郵地址",
                      value: "chillgoooo@gmail.com",
                      sub: "24小時內回覆",
                    },
                    {
                      icon: "ri-map-pin-line",
                      label: "辦公地址",
                      value: "香港九龍旺角彌敦道 688號",
                      sub: "18樓 1801室",
                    },
                    {
                      icon: "ri-time-line",
                      label: "辦公時間",
                      value: "週一至週五 10:00–19:00",
                      sub: "週末及公眾假期 11:00–17:00",
                    },
                  ].map((info) => (
                    <div key={info.label} className="flex items-start space-x-4">
                      <div className="w-11 h-11 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${info.icon} text-orange-500 text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">{info.label}</p>
                        <p className="text-gray-900 font-medium text-sm">{info.value}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{info.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-orange-500 rounded-2xl p-6 text-white">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold">專屬行程顧問</p>
                    <p className="text-white/70 text-xs">一對一量身訂製服務</p>
                  </div>
                </div>
                <p className="text-white/85 text-sm leading-relaxed">
                  填寫表單後，我們的專業顧問將在 <strong className="text-white">24小時內</strong> 與您聯繫，根據您的需求及預算，為您量身規劃最完美的自駕旅程。
                </p>
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8">
                {contactSuccess ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                      <i className="ri-check-line text-green-500 text-4xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">查詢已送出！</h3>
                    <p className="text-gray-600 mb-8 max-w-sm">
                      感謝您的查詢，我們的顧問將在 24 小時內透過電郵或電話與您聯繫。
                    </p>
                    <button
                      onClick={() => setContactSuccess(false)}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap"
                    >
                      再次查詢
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleContactSubmit}
                    className="space-y-5"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-6">發送查詢</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          姓名 <span className="text-orange-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="請輸入您的姓名"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          電話號碼
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="+852 XXXX XXXX"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        電郵地址 <span className="text-orange-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="example@email.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          感興趣的目的地
                        </label>
                        <select
                          name="destination"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white cursor-pointer"
                        >
                          <option value="">請選擇目的地</option>
                          <option>海南島環島</option>
                          <option>川藏線</option>
                          <option>青海湖</option>
                          <option>獨庫公路穿越</option>
                          <option>內蒙古草原</option>
                          <option>新疆天山</option>
                          <option>其他目的地</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          出發人數
                        </label>
                        <select
                          name="travelers"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white cursor-pointer"
                        >
                          <option value="">請選擇人數</option>
                          <option>1 人</option>
                          <option>2 人</option>
                          <option>3–4 人</option>
                          <option>5–8 人</option>
                          <option>9 人或以上</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        行程需求 <span className="text-orange-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="請描述您的行程需求，例如：出發日期、行程天數、特別要求、預算範圍等……"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all resize-none"
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">最多 500 字</p>
                    </div>

                    <button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap"
                    >
                      {contactSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          <span>送出中…</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          <span>送出查詢</span>
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-400">
                      提交即表示您同意我們的
                      <span className="text-orange-500 cursor-pointer"> 私隱政策 </span>
                      及
                      <span className="text-orange-500 cursor-pointer"> 服務條款</span>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-800 text-white">
        {/* Main Footer */}
        <div className="w-full px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-5">
                <img
                  src="https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png"
                  alt="Chill Go Logo"
                  className="h-16 w-auto object-contain"
                />
              </div>
              <p className="text-stone-400 text-sm leading-relaxed mb-6 max-w-xs">
                香港首屈一指的自駕遊平台，深耕自駕旅遊領域逾 15 年，致力為旅者打造最難忘的山海之旅。
              </p>

              {/* Social Icons */}
              <div className="flex items-center space-x-3">
                {[
                  { icon: "ri-facebook-fill", label: "Facebook", href: "https://www.facebook.com/profile.php?id=61582054874473" },
                  { icon: "ri-instagram-line", label: "Instagram", href: "https://www.instagram.com/chill_goooo?igsh=aTdtemhyNWNlYjU2&utm_source=qr" },
                  { icon: "ri-youtube-fill", label: "YouTube", href: "#" },
                  { icon: "ri-wechat-fill", label: "WeChat", href: "#" },
                  { icon: "ri-tiktok-fill", label: "TikTok", href: "#" },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="nofollow noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 flex items-center justify-center bg-stone-700 hover:bg-orange-500 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className={`${social.icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Links Columns */}
            {[
              {
                title: "熱門目的地",
                links: [
                  { label: "海南島環島", action: () => scrollToSection("destinations") },
                  { label: "川藏線深度遊", action: () => scrollToSection("destinations") },
                  { label: "青海湖親子遊", action: () => scrollToSection("destinations") },
                  { label: "獨庫公路穿越", action: () => scrollToSection("destinations") },
                  { label: "內蒙古草原", action: () => scrollToSection("destinations") },
                  { label: "新疆天山", action: () => scrollToSection("destinations") },
                ],
              },
              {
                title: "我們的服務",
                links: [
                  { label: "行程規劃", action: () => navigate("/custom-itinerary") },
                  { label: "車輛租用", action: () => scrollToSection("info") },
                  { label: "專業領隊", action: () => scrollToSection("info") },
                  { label: "住宿安排", action: () => scrollToSection("info") },
                  { label: "緊急救援", action: () => scrollToSection("info") },
                  { label: "保險方案", action: () => scrollToSection("pricing") },
                ],
              },
              {
                title: "關於我們",
                links: [
                  { label: "公司介紹", action: () => scrollToSection("home") },
                  { label: "旅行攻略", action: () => scrollToSection("info") },
                  { label: "客戶評價", action: () => scrollToSection("home") },
                  { label: "媒體報道", action: () => scrollToSection("home") },
                  { label: "加入我們", action: () => scrollToSection("contact") },
                  { label: "聯繫我們", action: () => scrollToSection("contact") },
                ],
              },
            ].map((col) => (
              <div key={col.title}>
                <h4 className="text-white font-semibold text-sm mb-5 tracking-wide">
                  {col.title}
                </h4>
                <ul className="space-y-3">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <button
                        onClick={link.action}
                        className="text-stone-400 hover:text-orange-400 text-sm transition-colors cursor-pointer text-left"
                      >
                        {link.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter strip */}
          <div className="mt-14 pt-10 border-t border-stone-700">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <p className="text-white font-semibold mb-1">訂閱旅遊資訊</p>
                <p className="text-stone-400 text-sm">獲取最新路線推薦與限時優惠，不定期發送，隨時可退訂。</p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <input
                  type="email"
                  placeholder="輸入您的電郵地址"
                  className="flex-1 md:w-64 bg-stone-700 border border-stone-600 text-white placeholder-stone-400 rounded-full px-5 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                />
                <button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                  訂閱
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-stone-700">
          <div className="w-full px-6 lg:px-12 py-5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-stone-500 text-xs">
              <p>© {new Date().getFullYear()} Chill Go Limited. 版權所有，不得轉載。</p>
              <div className="flex items-center space-x-5">
                {["私隱政策", "服務條款", "Cookie 政策", "網站地圖"].map((item) => (
                  <a
                    key={item}
                    href="#"
                    rel="nofollow"
                    className="hover:text-orange-400 transition-colors"
                  >
                    {item}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />

      {/* All Destinations Modal */}
      {showAllDestinations && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAllDestinations(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowAllDestinations(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>

            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mb-5">
                <i className="ri-search-line text-stone-400 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">找不到符合條件的路線</h3>
              <p className="text-gray-500 text-sm mb-6">試試調整篩選條件，探索更多精彩目的地</p>
              <button
                onClick={() => {
                  setShowAllDestinations(false);
                  setShowBookingModal(true);
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                重置所有篩選
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { setShowBookingModal(false); setBookingSuccess(false); }}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="relative overflow-hidden rounded-t-2xl bg-stone-900 px-8 pt-8 pb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20winding%20mountain%20road%20through%20golden%20Tibetan%20plateau%20at%20sunrise%2C%20dramatic%20landscape%20with%20orange%20sky%2C%20adventure%20travel%20photography%2C%20cinematic%20wide%20shot&width=600&height=200&seq=9901&orientation=landscape"
                alt="booking header"
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
              <button
                onClick={() => { setShowBookingModal(false); setBookingSuccess(false); }}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-lg"></i>
              </button>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-orange-500/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                  <span>立即預訂 · 24小時內回覆</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">開啟您的自駕之旅</h3>
                <p className="text-white/70 text-sm">填寫下方資料，顧問將免費為您量身規劃行程</p>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-8 py-7">
              {bookingSuccess ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-check-line text-green-500 text-3xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">預訂申請已送出！</h4>
                  <p className="text-gray-500 text-sm mb-2 max-w-xs">
                    感謝您的查詢，我們的顧問將在 <strong className="text-orange-500">24小時內</strong> 與您聯繫。
                  </p>
                  <p className="text-gray-400 text-xs mb-7">如需即時回覆，歡迎直接 WhatsApp 聯絡我們</p>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={() => { setBookingSuccess(false); setShowBookingModal(false); }}
                      className="flex-1 border-2 border-gray-200 text-gray-600 hover:border-gray-300 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                    >
                      返回首頁
                    </button>
                    <a
                      href="https://wa.me/85253482337"
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line text-base"></i>
                      <span>WhatsApp 聯繫</span>
                    </a>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleBookingSubmit} className="space-y-4">
                  {/* Name + Phone */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">
                        姓名 <span className="text-orange-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="您的姓名"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        電話
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+852 XXXX XXXX"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      電郵 <span className="text-orange-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                    />
                  </div>

                  {/* Destination + Travelers */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">
                        心儀目的地
                      </label>
                      <select
                        name="destination"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white cursor-pointer"
                      >
                        <option value="">請選擇</option>
                        <option>川藏線</option>
                        <option>青海湖</option>
                        <option>獨庫公路</option>
                        <option>海南島環島</option>
                        <option>新疆天山</option>
                        <option>西藏珠峰</option>
                        <option>歐洲路線</option>
                        <option>尚未決定</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">
                        出行人數
                      </label>
                      <select
                        name="travelers"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white cursor-pointer"
                      >
                        <option value="">請選擇</option>
                        <option>1 人</option>
                        <option>2 人</option>
                        <option>3–4 人</option>
                        <option>5–8 人</option>
                        <option>9 人或以上</option>
                      </select>
                    </div>
                  </div>

                  {/* Expected date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      預計出發月份
                    </label>
                    <select
                      name="travel_date"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white cursor-pointer"
                    >
                      <option value="">請選擇</option>
                      <option>2026年4月</option>
                      <option>2026年5月</option>
                      <option>2026年6月</option>
                      <option>2026年7月</option>
                      <option>2026年8月</option>
                      <option>2026年9月</option>
                      <option>2026年10月</option>
                      <option>2026年11月或以後</option>
                      <option>待定</option>
                    </select>
                  </div>

                  {/* Remarks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      備註（選填）
                    </label>
                    <textarea
                      name="remarks"
                      rows={2}
                      maxLength={500}
                      placeholder="特別要求、預算範圍、其他問題……"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all resize-none"
                    />
                  </div>

                  {/* Perks strip */}
                  <div className="flex items-center justify-around bg-stone-50 rounded-xl px-4 py-3">
                    {[
                      { icon: "ri-shield-check-line", text: "免費諮詢" },
                      { icon: "ri-refresh-line", text: "無限次修改" },
                      { icon: "ri-time-line", text: "24小時回覆" },
                    ].map((p) => (
                      <div key={p.text} className="flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${p.icon} text-orange-500 text-sm`}></i>
                        </div>
                        <span className="text-gray-600 text-xs font-medium">{p.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={bookingSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    {bookingSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>送出中…</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        <span>立即預訂諮詢</span>
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    提交即同意我們的<span className="text-orange-500 cursor-pointer"> 私隱政策</span>及<span className="text-orange-500 cursor-pointer"> 服務條款</span>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-8 right-8 z-50 bg-green-500 text-white px-6 py-4 rounded-xl shadow-xl flex items-center space-x-3 animate-in fade-in slide-in-from-bottom-4">
          <i className="ri-check-circle-line text-2xl"></i>
          <div>
            <div className="font-semibold">提交成功</div>
            <div className="text-sm opacity-90">我們將在24小時內與您聯繫</div>
          </div>
        </div>
      )}

      {/* Destination Detail Modal */}
      {selectedDetail && (
        <DestinationDetailModal
          destination={selectedDetail}
          onClose={() => setSelectedDetailId(null)}
          onBook={() => {
            setSelectedDetailId(null);
            setShowBookingModal(true);
          }}
        />
      )}
    </div>
  );
};

export default HomePage;